import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api, API, idr, pesanGalat } from "../lib/api";
import { toast } from "sonner";
import {
  FileBarChart, Search, Download, FileText, ArrowLeft, ExternalLink, Play,
  Info, AlertTriangle, Layers, RefreshCw,
} from "lucide-react";
import LoadingState from "@/components/ui/loading-state";
import { bulanIniISO, hariIniISO } from "../lib/tanggal";

/**
 * Pusat Laporan — brief owner 10 Sep 2026 poin 2.
 *
 * Satu tempat untuk memeriksa SEMUA laporan sistem, bergaya Accurate: panel
 * parameter seragam, tabel seragam, export Excel/PDF untuk setiap laporan.
 *
 * Halaman ini TIDAK punya kunci izin sendiri — isinya disaring per-laporan oleh
 * backend lewat izin yang sudah ada (`/laporan/katalog` hanya mengembalikan
 * yang boleh dilihat pemanggil). Jadi tidak ada tombol di sini yang ujungnya
 * 403, dan tidak ada 61 karyawan yang harus dicentangi ulang sebelum fitur ini
 * terlihat oleh siapa pun.
 *
 * Angka ringkasan datang APA ADANYA dari server. Ia TIDAK boleh dijumlah dari
 * baris yang tampil: baris bisa terpotong, dan total dari halaman terpotong
 * adalah cara paling halus membuat laporan berbohong.
 */

const inp = "h-10 px-3 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-200 bg-white";

function nilaiTampil(v, tipe) {
  if (v === null || v === undefined || v === "") return "—";
  if (tipe === "uang" && typeof v === "number") return idr(v);
  if (tipe === "angka" && typeof v === "number") return v.toLocaleString("id-ID");
  if (tipe === "persen" && typeof v === "number") return `${v.toFixed(1)}%`;
  if (typeof v === "boolean") return v ? "Ya" : "Tidak";
  return String(v);
}

/** Panel parameter dibangun dari deklarasi server, bukan dari daftar di layar —
 *  supaya menambah laporan baru di backend tidak perlu menyentuh berkas ini. */
function Parameter({ spec, nilai, ubah, gudang, cabang, akun }) {
  const set = (k, v) => ubah({ ...nilai, [k]: v });
  return (
    <div className="flex flex-wrap gap-2 items-end">
      {spec.map(p => (
        <label key={p.nama} className="text-xs text-slate-500 flex flex-col gap-1">
          <span>
            {p.label}
            {p.wajib && <span className="text-rose-500"> *</span>}
          </span>
          {p.tipe === "bulan" ? (
            <input type="month" value={nilai[p.nama] || ""} className={inp}
              data-testid={`pl-param-${p.nama}`}
              onChange={e => set(p.nama, e.target.value)} />
          ) : p.tipe === "tanggal" ? (
            <input type="date" value={nilai[p.nama] || ""} className={inp}
              data-testid={`pl-param-${p.nama}`}
              onChange={e => set(p.nama, e.target.value)} />
          ) : p.tipe === "cabang" ? (
            <select value={nilai[p.nama] || ""} className={inp}
              data-testid={`pl-param-${p.nama}`}
              onChange={e => set(p.nama, e.target.value)}>
              <option value="">Semua cabang</option>
              {cabang.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          ) : p.tipe === "akun" ? (
            // Akun COA dipilih dari daftar, BUKAN diketik: parameternya berupa
            // UUID: menyuruh orang mengetiknya sama saja dengan mematikan
            // laporannya. Daftar akun ikut dimuat dari endpoint yang sudah ada
            // (`/finance/buku-besar/akun`) — kalau akunnya tidak boleh dilihat,
            // daftarnya kosong dan laporannya memang bukan hak akun ini.
            <select value={nilai[p.nama] || ""} className={inp}
              data-testid={`pl-param-${p.nama}`}
              onChange={e => set(p.nama, e.target.value)}>
              <option value="">— pilih akun —</option>
              {akun.map(a => (
                <option key={a.id} value={a.id}>{a.code} — {a.name}</option>
              ))}
            </select>
          ) : p.tipe === "gudang" ? (
            <select value={nilai[p.nama] || ""} className={inp}
              data-testid={`pl-param-${p.nama}`}
              onChange={e => set(p.nama, e.target.value)}>
              <option value="">Semua gudang</option>
              {gudang.map(g => <option key={g.id} value={g.id}>{g.nama}</option>)}
            </select>
          ) : (
            <input type="text" value={nilai[p.nama] || ""} className={inp}
              data-testid={`pl-param-${p.nama}`} placeholder={p.label}
              onChange={e => set(p.nama, e.target.value)} />
          )}
        </label>
      ))}
    </div>
  );
}

export default function PusatLaporan() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [katalog, setKatalog] = useState(null);
  const [cari, setCari] = useState("");
  const [kategoriAktif, setKategoriAktif] = useState("");
  const [pilih, setPilih] = useState(null);       // entri katalog yang dibuka
  const [param, setParam] = useState({});
  const [hasil, setHasil] = useState(null);
  const [jalan, setJalan] = useState(false);
  const [unduh, setUnduh] = useState("");
  const [gudang, setGudang] = useState([]);
  const [cabangOpsi, setCabangOpsi] = useState([]);
  const [akunOpsi, setAkunOpsi] = useState([]);

  useEffect(() => {
    api.get("/laporan/katalog")
      .then(r => setKatalog(r.data))
      .catch(e => toast.error(pesanGalat(e, "Gagal memuat daftar laporan")));
    api.get("/inventory/cabang-options")
      .then(r => setCabangOpsi(r.data.items || [])).catch(() => {});
    api.get("/inventory/warehouses")
      .then(r => setGudang(r.data.items || [])).catch(() => {});
    // Daftar akun COA untuk parameter bertipe "akun" (Buku Besar). Gagal
    // diam-diam kalau tidak berhak — laporannya sendiri memang tidak akan
    // muncul di katalog untuk akun seperti itu.
    api.get("/finance/buku-besar/akun")
      .then(r => setAkunOpsi(Array.isArray(r.data) ? r.data : (r.data.items || [])))
      .catch(() => {});
  }, []);

  // Bawaan parameter: bulan & tanggal hari ini lewat helper WIB, BUKAN
  // `new Date().toISOString()` — yang terakhir selalu UTC dan memberi tanggal
  // kemarin bagi pengguna WIB antara 00:00-07:00.
  const bawaanParam = useCallback((entri) => {
    const p = {};
    for (const s of entri.params || []) {
      if (s.tipe === "bulan") p[s.nama] = bulanIniISO();
      if (s.tipe === "tanggal") p[s.nama] = hariIniISO();
    }
    return p;
  }, []);

  const buka = useCallback((entri) => {
    if (entri.mode === "tautan") { navigate(entri.rute); return; }
    setPilih(entri);
    setHasil(null);
    setParam(bawaanParam(entri));
    setSearchParams(prev => {
      const n = new URLSearchParams(prev);
      n.set("id", entri.id);
      return n;
    }, { replace: true });
  }, [navigate, bawaanParam, setSearchParams]);

  // Tautan langsung ke satu laporan (?id=...) — dipakai dari dashboard/bookmark.
  const idUrl = searchParams.get("id");
  const dibukaOtomatis = useRef(false);
  useEffect(() => {
    if (!katalog || !idUrl || dibukaOtomatis.current) return;
    const e = (katalog.items || []).find(x => x.id === idUrl);
    if (e) { dibukaOtomatis.current = true; buka(e); }
  }, [katalog, idUrl, buka]);

  const tutup = () => {
    setPilih(null); setHasil(null);
    setSearchParams(prev => {
      const n = new URLSearchParams(prev);
      n.delete("id");
      return n;
    }, { replace: true });
  };

  const jalankan = useCallback(async (entri, p) => {
    setJalan(true);
    try {
      const r = await api.post("/laporan/jalankan", { id: entri.id, parameter: p });
      setHasil(r.data);
    } catch (e) {
      setHasil(null);
      toast.error(pesanGalat(e, "Laporan gagal dijalankan"));
    } finally {
      setJalan(false);
    }
  }, []);

  // Laporan dijalankan otomatis begitu dibuka — owner memilih laporan untuk
  // MELIHATNYA, bukan untuk menekan tombol kedua. Laporan yang punya parameter
  // wajib menunggu parameternya terisi dulu.
  useEffect(() => {
    if (!pilih || hasil || jalan) return;
    const kurang = (pilih.params || []).filter(s => s.wajib && !param[s.nama]);
    if (kurang.length) return;
    jalankan(pilih, param);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pilih]);

  const unduhBerkas = async (format) => {
    if (!pilih) return;
    setUnduh(format);
    try {
      const q = new URLSearchParams({ format });
      Object.entries(param).forEach(([k, v]) => { if (v) q.set(k, v); });
      const token = localStorage.getItem("hris_token");
      const res = await fetch(`${API}/laporan/${pilih.id}/export?${q}`,
        { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error("gagal");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${pilih.nama.replace(/[^\w]+/g, "_")}.${format}`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success(`${format.toUpperCase()} terunduh`);
    } catch {
      toast.error(`Gagal mengunduh ${format.toUpperCase()}`);
    } finally {
      setUnduh("");
    }
  };

  const terlihat = useMemo(() => {
    if (!katalog) return [];
    const t = cari.trim().toLowerCase();
    return (katalog.items || []).filter(e =>
      (!kategoriAktif || e.kategori === kategoriAktif) &&
      (!t || e.nama.toLowerCase().includes(t) || e.ringkas.toLowerCase().includes(t)
        || e.kategori.toLowerCase().includes(t)));
  }, [katalog, cari, kategoriAktif]);

  const kelompok = useMemo(() => {
    const g = {};
    for (const e of terlihat) (g[e.kategori] = g[e.kategori] || []).push(e);
    return g;
  }, [terlihat]);

  if (!katalog) {
    return <div className="p-6"><LoadingState /></div>;
  }

  // ------------------------------------------------------------- satu laporan
  if (pilih) {
    const kol = hasil?.kolom || [];
    return (
      <div className="p-4 md:p-6 space-y-4" data-testid="pusat-laporan-detail">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="flex items-start gap-3">
            <button type="button" onClick={tutup} data-testid="lap-kembali"
              className="h-10 w-10 grid place-items-center rounded-lg border border-slate-200 hover:bg-slate-50">
              <ArrowLeft className="w-4 h-4" />
            </button>
            <div>
              <div className="text-[11px] uppercase tracking-wide text-emerald-600 font-semibold">
                {pilih.kategori}
              </div>
              <h1 className="text-xl font-bold text-slate-900">{pilih.nama}</h1>
              <p className="text-sm text-slate-500 max-w-2xl">{pilih.ringkas}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {pilih.rute && (
              <button type="button" onClick={() => navigate(pilih.rute)}
                data-testid="lap-buka-halaman"
                className="h-10 px-3 rounded-lg border border-slate-200 text-sm text-slate-600 hover:bg-slate-50 inline-flex items-center gap-2">
                <ExternalLink className="w-4 h-4" /> Halaman aslinya
              </button>
            )}
            <button type="button" onClick={() => unduhBerkas("xlsx")} disabled={!!unduh || !hasil}
              data-testid="lap-export-xlsx"
              className="h-10 px-3 rounded-lg text-sm font-semibold bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50 inline-flex items-center gap-2">
              <Download className="w-4 h-4" /> {unduh === "xlsx" ? "Menyiapkan…" : "Excel"}
            </button>
            <button type="button" onClick={() => unduhBerkas("pdf")} disabled={!!unduh || !hasil}
              data-testid="lap-export-pdf"
              className="h-10 px-3 rounded-lg text-sm font-semibold border border-slate-200 text-slate-700 hover:bg-slate-50 disabled:opacity-50 inline-flex items-center gap-2">
              <FileText className="w-4 h-4" /> {unduh === "pdf" ? "Menyiapkan…" : "PDF"}
            </button>
          </div>
        </div>

        {(pilih.params || []).length > 0 && (
          <div className="bg-white border border-slate-200 rounded-xl p-3 flex flex-wrap items-end gap-3">
            <Parameter spec={pilih.params} nilai={param} ubah={setParam}
              gudang={gudang} cabang={cabangOpsi} akun={akunOpsi} />
            <button type="button" onClick={() => jalankan(pilih, param)} disabled={jalan}
              data-testid="lap-jalankan"
              className="h-10 px-4 rounded-lg text-sm font-semibold bg-slate-900 text-white hover:bg-slate-800 disabled:opacity-60 inline-flex items-center gap-2">
              {jalan ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />}
              Tampilkan
            </button>
          </div>
        )}

        {/* Ringkasan datang APA ADANYA dari laporannya — tidak pernah dijumlah
            dari baris yang tampil (baris bisa terpotong). Tiap kartu membawa
            TIPE-nya sendiri: tanpa itu layar harus menebak, dan menebak semua
            angka sebagai rupiah menghasilkan "Margin Kotor %: Rp 0". */}
        {hasil && (hasil.ringkasan || []).length > 0 && (
          <div className="flex flex-wrap gap-2" data-testid="lap-ringkasan">
            {hasil.ringkasan.map((r) => (
              <div key={r.label} className="bg-white border border-slate-200 rounded-xl px-4 py-2 min-w-[150px]">
                <div className="text-[11px] text-slate-500">{r.label}</div>
                <div className="text-base font-bold text-slate-900">
                  {nilaiTampil(r.nilai, r.tipe)}
                </div>
              </div>
            ))}
          </div>
        )}

        {(hasil?.catatan || []).map((c, i) => (
          <div key={i} data-testid="lap-catatan"
            className="flex items-start gap-2 text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
            <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" /><span>{c}</span>
          </div>
        ))}
        {hasil?.terpotong && (
          <div data-testid="lap-terpotong"
            className="flex items-start gap-2 text-sm text-slate-700 bg-slate-100 border border-slate-200 rounded-lg px-3 py-2">
            <Info className="w-4 h-4 mt-0.5 shrink-0" />
            <span>
              Ditampilkan {hasil.baris.length} dari {hasil.total_baris} baris.
              Persempit filternya, atau unduh Excel untuk data lengkap.
            </span>
          </div>
        )}

        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
          {jalan ? <LoadingState /> : !hasil ? (
            <div className="p-10 text-center text-slate-500 text-sm" data-testid="lap-belum-jalan">
              Isi parameternya lalu tekan <b>Tampilkan</b>.
            </div>
          ) : (hasil.baris || []).length === 0 ? (
            <div className="p-10 text-center text-slate-500 text-sm" data-testid="lap-kosong">
              <Layers className="w-8 h-8 mx-auto mb-2 text-slate-300" />
              Tidak ada data untuk parameter ini.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="lap-tabel">
                <thead className="bg-slate-50 text-slate-600 border-b border-slate-200">
                  <tr>
                    {kol.map(c => (
                      <th key={c.kunci}
                        className={`px-3 py-2 font-semibold whitespace-nowrap ${
                          c.align === "kanan" ? "text-right" : "text-left"}`}>
                        {c.label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {hasil.baris.map((b, i) => (
                    <tr key={i} className={b._tebal ? "bg-slate-50 font-semibold" : "hover:bg-slate-50/60"}>
                      {kol.map((c, ci) => (
                        <td key={c.kunci}
                          className={`px-3 py-2 ${c.align === "kanan" ? "text-right whitespace-nowrap" : ""}`}
                          style={ci === 0 && b._dalam ? { paddingLeft: 12 + b._dalam * 18 } : undefined}>
                          {nilaiTampil(b[c.kunci], c.tipe)}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    );
  }

  // ------------------------------------------------------------- daftar semua
  return (
    <div className="p-4 md:p-6 space-y-4" data-testid="pusat-laporan">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-emerald-50 border border-emerald-100 grid place-items-center">
            <FileBarChart className="w-5 h-5 text-emerald-600" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-slate-900">Pusat Laporan</h1>
            <p className="text-sm text-slate-500">
              Semua laporan sistem di satu tempat — {katalog.count} laporan bisa Anda buka
              {katalog.total_terdaftar > katalog.count &&
                ` (dari ${katalog.total_terdaftar} yang terdaftar; sisanya di luar izin akun Anda)`}.
            </p>
          </div>
        </div>
        <div className="relative min-w-[260px]">
          <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <input value={cari} onChange={e => setCari(e.target.value)} data-testid="lap-cari"
            placeholder="Cari laporan… (mis. neraca, piutang, stok)"
            className={`${inp} w-full pl-9`} />
        </div>
      </div>

      <div className="flex flex-wrap gap-1">
        <button type="button" onClick={() => setKategoriAktif("")} data-testid="lap-kat-semua"
          className={`h-9 px-3 rounded-lg text-sm border ${
            kategoriAktif === "" ? "bg-slate-900 text-white border-slate-900"
                                 : "bg-white border-slate-200 text-slate-600 hover:bg-slate-50"}`}>
          Semua ({katalog.count})
        </button>
        {(katalog.kategori || []).map(kt => (
          <button key={kt.nama} type="button" onClick={() => setKategoriAktif(kt.nama)}
            data-testid={`lap-kat-${kt.nama}`}
            className={`h-9 px-3 rounded-lg text-sm border ${
              kategoriAktif === kt.nama ? "bg-slate-900 text-white border-slate-900"
                                        : "bg-white border-slate-200 text-slate-600 hover:bg-slate-50"}`}>
            {kt.nama} ({kt.jumlah})
          </button>
        ))}
      </div>

      {terlihat.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-xl p-10 text-center text-slate-500 text-sm"
          data-testid="lap-daftar-kosong">
          {katalog.count === 0
            ? "Akun Anda belum diberi izin untuk laporan mana pun. Hubungi admin."
            : "Tidak ada laporan yang cocok dengan pencarian ini."}
        </div>
      ) : (
        Object.entries(kelompok).map(([kat, isi]) => (
          <div key={kat} className="space-y-2">
            <h2 className="text-sm font-bold text-slate-700 uppercase tracking-wide">{kat}</h2>
            <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
              {isi.map(e => (
                <button key={e.id} type="button" onClick={() => buka(e)}
                  data-testid={`lap-kartu-${e.id}`}
                  className="text-left bg-white border border-slate-200 rounded-xl p-3 hover:border-emerald-300 hover:shadow-sm transition">
                  <div className="flex items-start justify-between gap-2">
                    <div className="font-semibold text-slate-900">{e.nama}</div>
                    {e.mode === "tautan" && (
                      <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-500 border border-slate-200 shrink-0"
                        title="Laporan ini dibuka di halamannya sendiri">
                        halaman
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-slate-500 mt-1 leading-relaxed">{e.ringkas}</div>
                </button>
              ))}
            </div>
          </div>
        ))
      )}
    </div>
  );
}
